import { createContext, useReducer, useCallback } from "react";

import axios from "../../utils/axios/v1/userAxios";
import { WafType } from "../../utils/constants";

const GET_LLM_EVENTS = "GET_LLM_EVENTS";
const GET_LLM_FW_EVENTS = "GET_LLM_FW_EVENTS";
const GET_LLM_RL_EVENTS = "GET_LLM_RL_EVENTS";
const GET_LLM_TOTAL_EVENTS_COUNT = "GET_LLM_TOTAL_EVENTS_COUNT";
const GET_LLM_TOTAL_FW_EVENTS_COUNT = "GET_LLM_TOTAL_FW_EVENTS_COUNT";
const GET_LLM_TOTAL_RL_EVENTS_COUNT = "GET_LLM_TOTAL_RL_EVENTS_COUNT";
const SET_LLM_CURRENT_STATUS = "SET_LLM_CURRENT_STATUS";
const GET_LLM_SINGLE_EVENT = "GET_LLM_SINGLE_EVENT";
const GET_LLM_SINGLE_FW_EVENT = "GET_LLM_SINGLE_FW_EVENT";
const GET_LLM_SINGLE_RL_EVENT = "GET_LLM_SINGLE_RL_EVENT";
const GET_LLM_EVENT_STATS = "GET_LLM_EVENT_STATS";
const GET_LLM_FW_EVENT_STATS = "GET_LLM_FW_EVENT_STATS";
const GET_LLM_RL_EVENT_STATS = "GET_LLM_RL_EVENT_STATS";
const SET_LLM_ERROR = "SET_LLM_ERROR";

const initialState = {
  zone_id: "all",
  time_range: { period: "7d" },
  events: null,
  event: null,
  fwEvents: null,
  fwEvent: null,
  rlEvents: null,
  rlEvent: null,
  llmEventStats: null,
  fwEventStats: null,
  rlEventStats: null,
  total_events_count: 0,
  total_fw_events_count: 0,
  total_rl_events_count: 0,
  rows_per_page: 5,
  from: 0,
  action: 3,
  conditions: [],
  errMsg: null,
};

const LLMEventReducer = (state, action) => {
  switch (action.type) {
    case GET_LLM_EVENTS:
      return {
        ...state,
        events: action.payload.events,
      };
    case GET_LLM_FW_EVENTS:
      return {
        ...state,
        fwEvents: action.payload.fwEvents,
      };
    case GET_LLM_RL_EVENTS:
      return {
        ...state,
        rlEvents: action.payload.rlEvents,
      };
    case GET_LLM_TOTAL_EVENTS_COUNT:
      return {
        ...state,
        total_events_count: action.payload.total_events_count,
      };
    case GET_LLM_TOTAL_FW_EVENTS_COUNT:
      return {
        ...state,
        total_fw_events_count: action.payload.total_fw_events_count,
      };
    case GET_LLM_TOTAL_RL_EVENTS_COUNT:
      return {
        ...state,
        total_rl_events_count: action.payload.total_rl_events_count,
      };
    case GET_LLM_SINGLE_EVENT:
      return {
        ...state,
        event: action.payload.event,
      };
    case GET_LLM_SINGLE_FW_EVENT:
      return {
        ...state,
        fwEvent: action.payload.fwEvent,
      };
    case GET_LLM_SINGLE_RL_EVENT:
      return {
        ...state,
        rlEvent: action.payload.rlEvent,
      };
    case GET_LLM_EVENT_STATS:
      return {
        ...state,
        llmEventStats: action.payload.llmEventStats,
      };
    case GET_LLM_FW_EVENT_STATS:
      return {
        ...state,
        fwEventStats: action.payload.fwEventStats,
      };
    case GET_LLM_RL_EVENT_STATS:
      return {
        ...state,
        rlEventStats: action.payload.rlEventStats,
      };
    case SET_LLM_CURRENT_STATUS:
      return {
        ...state,
        zone_id: action.payload.zone_id,
        time_range: action.payload.time_range,
        rows_per_page: action.payload.rows_per_page,
        from: action.payload.from,
        action: action.payload.action,
        conditions: action.payload.conditions,
      };
    case SET_LLM_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    default:
      return state;
  }
};

const LLMEventContext = createContext(null);

let tmpController;

function LLMEventProvider({ children }) {
  const [state, dispatch] = useReducer(LLMEventReducer, initialState);

  const setErr = useCallback((msg) => {
    dispatch({
      type: SET_LLM_ERROR,
      payload: {
        errMsg: msg,
      },
    });
  }, []);

  // Old getEvents callback, deprecated and not using now.
  const getEvents = useCallback(
    async (setLLMEventController, zone_id, time_range, rows_per_page, from, action, conditions, init = true) => {
      if (init) {
        dispatch({
          type: GET_LLM_EVENTS,
          payload: {
            events: null,
          },
        });
      }
      if (tmpController) tmpController.abort();
      const controller = new AbortController();
      const signal = controller.signal;
      setLLMEventController(controller);
      dispatch({
        type: SET_LLM_CURRENT_STATUS,
        payload: {
          zone_id: zone_id,
          time_range: time_range,
          rows_per_page: rows_per_page,
          from: from,
          action: action,
          conditions: conditions,
        },
      });
      dispatch({
        type: GET_LLM_TOTAL_EVENTS_COUNT,
        payload: {
          total_events_count: 0,
        },
      });
      try {
        const response = await axios.post(
          "llm_log/llm_event",
          {
            zone_id: zone_id,
            time_range: time_range,
            count: rows_per_page,
            from: from,
            conditions: conditions,
            action: action,
          },
          {
            signal,
          }
        );
        dispatch({
          type: GET_LLM_EVENTS,
          payload: {
            events: response.data.data,
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: response.data.total,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_LLM_EVENTS,
          payload: {
            events: [],
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: 0,
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );

  const getEvents2 = useCallback(
    async (setLLMEventController, zone_id, time_range, rows_per_page, from, conditions, init = true) => {
      if (init) {
        dispatch({
          type: GET_LLM_EVENTS,
          payload: {
            events: null,
          },
        });
      }
      if (tmpController) tmpController.abort();
      const controller = new AbortController();
      const signal = controller.signal;
      setLLMEventController(controller);
      dispatch({
        type: SET_LLM_CURRENT_STATUS,
        payload: {
          zone_id: zone_id,
          time_range: time_range,
          rows_per_page: rows_per_page,
          from: from,
          conditions: conditions,
        },
      });
      dispatch({
        type: GET_LLM_TOTAL_EVENTS_COUNT,
        payload: {
          total_events_count: 0,
        },
      });
      try {
        const response = await axios.post(
          "llm_log/llm_event2",
          {
            zone_id: zone_id,
            time_range: time_range,
            count: rows_per_page,
            from: from,
            conditions: conditions,
          },
          {
            signal,
          }
        );
        dispatch({
          type: GET_LLM_EVENTS,
          payload: {
            events: response.data.data,
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: response.data.total,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_LLM_EVENTS,
          payload: {
            events: [],
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: 0,
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getFwEvents = useCallback(
    async (setLLMEventController, zone_id, time_range, rows_per_page, from, conditions, init = true) => {
      if (init) {
        dispatch({
          type: GET_LLM_FW_EVENTS,
          payload: {
            fwEvents: null,
          },
        });
      }
      if (tmpController) tmpController.abort();
      const controller = new AbortController();
      const signal = controller.signal;
      setLLMEventController(controller);
      dispatch({
        type: SET_LLM_CURRENT_STATUS,
        payload: {
          zone_id: zone_id,
          time_range: time_range,
          rows_per_page: rows_per_page,
          from: from,
          conditions: conditions,
        },
      });
      dispatch({
        type: GET_LLM_TOTAL_FW_EVENTS_COUNT,
        payload: {
          total_fw_events_count: 0,
        },
      });
      try {
        const response = await axios.post(
          "llm_log/llm_fw_event",
          {
            zone_id: zone_id,
            time_range: time_range,
            count: rows_per_page,
            from: from,
            conditions: conditions,
          },
          {
            signal,
          }
        );
        dispatch({
          type: GET_LLM_FW_EVENTS,
          payload: {
            fwEvents: response.data.data,
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_FW_EVENTS_COUNT,
          payload: {
            total_fw_events_count: response.data.total,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_LLM_FW_EVENTS,
          payload: {
            fwEvents: [],
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_FW_EVENTS_COUNT,
          payload: {
            total_fw_events_count: 0,
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getOnlyFwEvents = useCallback(
    async (zone_id, time_range, rows_per_page, from, conditions, init = true) => {
      if (init) {
        dispatch({
          type: GET_LLM_FW_EVENTS,
          payload: {
            fwEvents: null,
          },
        });
      }
      if (tmpController) tmpController.abort();
      tmpController = new AbortController();
      const signal = tmpController.signal;
      dispatch({
        type: SET_LLM_CURRENT_STATUS,
        payload: {
          zone_id: zone_id,
          time_range: time_range,
          rows_per_page: rows_per_page,
          from: from,
          conditions: conditions,
        },
      });
      try {
        const response = await axios.post(
          "llm_log/llm_fw_event",
          {
            zone_id: zone_id,
            time_range: time_range,
            count: rows_per_page,
            from: from,
            conditions: conditions,
          },
          {
            signal,
          }
        );
        dispatch({
          type: GET_LLM_FW_EVENTS,
          payload: {
            fwEvents: response.data.data,
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_FW_EVENTS_COUNT,
          payload: {
            total_fw_events_count: response.data.total,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_LLM_FW_EVENTS,
          payload: {
            fwEvents: [],
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_FW_EVENTS_COUNT,
          payload: {
            total_fw_events_count: 0,
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getOnlyEvents = useCallback(
    async (zone_id, time_range, rows_per_page, from, action, conditions, init = true) => {
      if (init) {
        dispatch({
          type: GET_LLM_EVENTS,
          payload: {
            events: null,
          },
        });
      }
      if (tmpController) tmpController.abort();
      tmpController = new AbortController();
      const signal = tmpController.signal;
      dispatch({
        type: SET_LLM_CURRENT_STATUS,
        payload: {
          zone_id: zone_id,
          time_range: time_range,
          rows_per_page: rows_per_page,
          from: from,
          action: action,
          conditions: conditions,
        },
      });
      try {
        const response = await axios.post(
          "llm_log/llm_event",
          {
            zone_id: zone_id,
            time_range: time_range,
            count: rows_per_page,
            from: from,
            conditions: conditions,
            action: action,
          },
          {
            signal,
          }
        );
        dispatch({
          type: GET_LLM_EVENTS,
          payload: {
            events: response.data.data,
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: response.data.total,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_LLM_EVENTS,
          payload: {
            events: [],
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: 0,
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getOnlyEvents2 = useCallback(
    async (zone_id, time_range, rows_per_page, from, action, conditions, init = true) => {
      if (init) {
        dispatch({
          type: GET_LLM_EVENTS,
          payload: {
            events: null,
          },
        });
      }
      if (tmpController) tmpController.abort();
      tmpController = new AbortController();
      const signal = tmpController.signal;
      dispatch({
        type: SET_LLM_CURRENT_STATUS,
        payload: {
          zone_id: zone_id,
          time_range: time_range,
          rows_per_page: rows_per_page,
          from: from,
          action: action,
          conditions: conditions,
        },
      });
      try {
        const response = await axios.post(
          "llm_log/llm_event2",
          {
            zone_id: zone_id,
            time_range: time_range,
            count: rows_per_page,
            from: from,
            conditions: conditions,
            action: action,
          },
          {
            signal,
          }
        );
        dispatch({
          type: GET_LLM_EVENTS,
          payload: {
            events: response.data.data,
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: response.data.total,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_LLM_EVENTS,
          payload: {
            events: [],
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: 0,
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getEvent = useCallback(
    async (eventID) => {
      dispatch({
        type: GET_LLM_SINGLE_EVENT,
        payload: {
          event: null,
        },
      });
      try {
        const response = await axios.get(`llm_log/llm_event/${eventID}`);
        dispatch({
          type: GET_LLM_SINGLE_EVENT,
          payload: {
            event: response.data,
          },
        });
      } catch (err) {
        dispatch({
          type: GET_LLM_SINGLE_EVENT,
          payload: {
            event: null,
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getFwEvent = useCallback(
    async (eventID) => {
      dispatch({
        type: GET_LLM_SINGLE_FW_EVENT,
        payload: {
          fwEvent: null,
        },
      });
      try {
        const response = await axios.get(`llm_log/llm_fw_event/${eventID}`);
        dispatch({
          type: GET_LLM_SINGLE_FW_EVENT,
          payload: {
            fwEvent: response.data,
          },
        });
      } catch (err) {
        dispatch({
          type: GET_LLM_SINGLE_FW_EVENT,
          payload: {
            fwEvent: null,
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );

  const getRlEvents = useCallback(
    async (setLLMEventController, zone_id, time_range, rows_per_page, from, action, conditions, init = true) => {
      if (init) {
        dispatch({
          type: GET_LLM_RL_EVENTS,
          payload: {
            rlEvents: null,
          },
        });
      }
      if (tmpController) tmpController.abort();
      const controller = new AbortController();
      const signal = controller.signal;
      setLLMEventController(controller);
      dispatch({
        type: SET_LLM_CURRENT_STATUS,
        payload: {
          zone_id: zone_id,
          time_range: time_range,
          rows_per_page: rows_per_page,
          from: from,
          action: action,
          conditions: conditions,
        },
      });
      dispatch({
        type: GET_LLM_TOTAL_RL_EVENTS_COUNT,
        payload: {
          total_rl_events_count: 0,
        },
      });
      try {
        const response = await axios.post(
          "llm_log/llm_rl_event",
          {
            zone_id: zone_id,
            time_range: time_range,
            count: rows_per_page,
            from: from,
            action: action,
            conditions: conditions,
          },
          {
            signal,
          }
        );
        dispatch({
          type: GET_LLM_RL_EVENTS,
          payload: {
            rlEvents: response.data.data,
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_RL_EVENTS_COUNT,
          payload: {
            total_rl_events_count: response.data.total,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_LLM_RL_EVENTS,
          payload: {
            rlEvents: [],
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_RL_EVENTS_COUNT,
          payload: {
            total_rl_events_count: 0,
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );

  const getOnlyRlEvents = useCallback(
    async (zone_id, time_range, rows_per_page, from, action, conditions, init = true) => {
      if (init) {
        dispatch({
          type: GET_LLM_RL_EVENTS,
          payload: {
            rlEvents: null,
          },
        });
      }
      if (tmpController) tmpController.abort();
      tmpController = new AbortController();
      const signal = tmpController.signal;
      dispatch({
        type: SET_LLM_CURRENT_STATUS,
        payload: {
          zone_id: zone_id,
          time_range: time_range,
          rows_per_page: rows_per_page,
          from: from,
          action: action,
          conditions: conditions,
        },
      });
      try {
        const response = await axios.post(
          "llm_log/llm_rl_event",
          {
            zone_id: zone_id,
            time_range: time_range,
            count: rows_per_page,
            from: from,
            conditions: conditions,
            action: action,
          },
          {
            signal,
          }
        );
        dispatch({
          type: GET_LLM_RL_EVENTS,
          payload: {
            rlEvents: response.data.data,
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_RL_EVENTS_COUNT,
          payload: {
            total_rl_events_count: response.data.total,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_LLM_RL_EVENTS,
          payload: {
            rlEvents: [],
          },
        });
        dispatch({
          type: GET_LLM_TOTAL_RL_EVENTS_COUNT,
          payload: {
            total_rl_events_count: 0,
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );

  const getRlEvent = useCallback(
    async (eventID) => {
      dispatch({
        type: GET_LLM_SINGLE_RL_EVENT,
        payload: {
          rlEvent: null,
        },
      });
      try {
        const response = await axios.get(`llm_log/llm_rl_event/${eventID}`);
        dispatch({
          type: GET_LLM_SINGLE_RL_EVENT,
          payload: {
            rlEvent: response.data,
          },
        });
      } catch (err) {
        dispatch({
          type: GET_LLM_SINGLE_RL_EVENT,
          payload: {
            rlEvent: null,
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );

  const getLLMEventStats = useCallback(
    async (setLLMEventController, zone_id, time_range, conditions, init = true) => {
      if (init) {
        dispatch({
          type: GET_LLM_EVENT_STATS,
          payload: {
            llmEventStats: null,
          },
        });
      }
      const controller = new AbortController();
      const signal = controller.signal;
      setLLMEventController(controller);
      try {
        const response = await axios.post(
          "llm_stats/detection",
          {
            zone_id: zone_id,
            time_range: time_range,
            waf_type: WafType.ALL,
            conditions,
          },
          {
            signal,
          }
        );
        dispatch({
          type: GET_LLM_EVENT_STATS,
          payload: {
            llmEventStats: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_LLM_EVENT_STATS,
          payload: {
            llmEventStats: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getFwEventStats = useCallback(
    async (setLLMEventController, zone_id, time_range, conditions, init = true) => {
      if (init) {
        dispatch({
          type: GET_LLM_FW_EVENT_STATS,
          payload: {
            fwEventStats: null,
          },
        });
      }
      const controller = new AbortController();
      const signal = controller.signal;
      setLLMEventController(controller);
      try {
        const response = await axios.post(
          "llm_fw_stats/stats",
          {
            zone_id: zone_id,
            time_range: time_range,
            waf_type: WafType.ALL,
            conditions,
          },
          {
            signal,
          }
        );
        dispatch({
          type: GET_LLM_FW_EVENT_STATS,
          payload: {
            fwEventStats: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_LLM_FW_EVENT_STATS,
          payload: {
            fwEventStats: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getRlEventStats = useCallback(
    async (setLLMEventController, zone_id, time_range, conditions, init = true) => {
      if (init) {
        dispatch({
          type: GET_LLM_RL_EVENT_STATS,
          payload: {
            rlEventStats: null,
          },
        });
      }
      const controller = new AbortController();
      const signal = controller.signal;
      setLLMEventController(controller);
      try {
        const response = await axios.post(
          "llm_rl_stats/stats",
          {
            zone_id: zone_id,
            time_range: time_range,
            conditions,
          },
          {
            signal,
          }
        );
        dispatch({
          type: GET_LLM_RL_EVENT_STATS,
          payload: {
            rlEventStats: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_LLM_RL_EVENT_STATS,
          payload: {
            rlEventStats: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  return (
    <LLMEventContext.Provider
      value={{
        ...state,
        getEvents,
        getEvents2,
        getEvent,
        getFwEvents,
        getOnlyFwEvents,
        getFwEvent,
        getOnlyEvents,
        getOnlyEvents2,
        getRlEvents,
        getOnlyRlEvents,
        getRlEvent,
        getLLMEventStats,
        getFwEventStats,
        getRlEventStats,
        setErr,
      }}
    >
      {children}
    </LLMEventContext.Provider>
  );
}

export { LLMEventContext, LLMEventProvider };
